import { LibraryItemCardSkeleton } from "@/components/skeletons";
import { LibraryItemCard } from "@/components/teacher/LibraryItemCard";
import { TeacherLibraryChildrenDocument, TeacherLibraryRootDocument } from "@/generated/graphql";
import { format, useT } from "@/i18n";
import { type AssignableReach, projectBlockedReason } from "@/lib/utils/assignableReach";
import { useQuery } from "@apollo/client/react";
import { useState } from "react";
import { HiChevronRight } from "react-icons/hi";

const PAGE_SIZE = 100;

export interface LibraryLesson {
  id: string;
  name: string;
  slideCount: number;
  parentId: string | null;
  path: string;
}

interface Folder {
  id: string;
  name: string;
}

interface LibraryBrowserProps {
  onOpenLesson: (lesson: LibraryLesson) => void;
  /** Marks the projects an assignment cannot use. Omitted browses the teacher's own library. */
  reach?: AssignableReach | undefined;
  /** True while the reach is still being read, which no project may be called pickable before. */
  reachLoading?: boolean | undefined;
}

/** Browses projects and lessons from the root down. Folders are projects; leaves are lessons. */
export function LibraryBrowser({ onOpenLesson, reach, reachLoading }: LibraryBrowserProps) {
  const t = useT();
  const [trail, setTrail] = useState<Folder[]>([]);
  const parent = trail.at(-1) ?? null;

  const rootQuery = useQuery(TeacherLibraryRootDocument, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    skip: parent !== null,
  });
  const childQuery = useQuery(TeacherLibraryChildrenDocument, {
    variables: { parentId: parent?.id ?? null, limit: PAGE_SIZE, offset: 0 },
    skip: parent === null,
  });
  const { error } = parent === null ? rootQuery : childQuery;
  // A project may not render as pickable before the reach has answered, so the grid waits on it
  // too — it is a MyBestUser round trip, and the library list is only a local read.
  const loading = (parent === null ? rootQuery.loading : childQuery.loading) || reachLoading;
  const projects =
    parent === null ? rootQuery.data?.rootProjects : childQuery.data?.projectsByParentId;
  const lessons =
    parent === null ? rootQuery.data?.rootLessons : childQuery.data?.lessonsByParentId;

  const truncated = (projects?.hasMore ?? false) || (lessons?.hasMore ?? false);

  const blockedReason = (project: { productIds: string[]; level: number }) => {
    const reasonKey = reach ? projectBlockedReason(project, reach) : null;
    return reasonKey ? t[reasonKey] : undefined;
  };

  return (
    <div className="flex flex-col gap-4 min-h-0">
      <nav className="flex items-center gap-1 flex-wrap caption text-app-muted">
        <button
          type="button"
          onClick={() => setTrail([])}
          className="font-semibold hover:text-app-ink"
        >
          {t["library.root"]}
        </button>
        {trail.map((folder, index) => (
          <span key={folder.id} className="flex items-center gap-1">
            <HiChevronRight className="w-3 h-3" />
            <button
              type="button"
              onClick={() => setTrail(trail.slice(0, index + 1))}
              className={
                index === trail.length - 1
                  ? "app-value text-app-ink"
                  : "font-semibold hover:text-app-ink"
              }
            >
              {folder.name}
            </button>
          </span>
        ))}
      </nav>

      {error && <p className="body-sm text-app-overdue">{t["library.loadError"]}</p>}

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 items-start p-0 sm:p-5">
          {Array.from({ length: 16 }, (_, index) => index).map((key) => (
            <LibraryItemCardSkeleton key={key} />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 overflow-y-auto items-start p-0 sm:p-5">
          {projects?.items.map((project) => (
            <LibraryItemCard
              key={project.id}
              name={project.name}
              imageUrl={project.imageUrl}
              subtitle={format(
                project.itemCount === 1 ? t["library.itemOne"] : t["library.itemOther"],
                { count: project.itemCount }
              )}
              blockedReason={blockedReason(project)}
              onClick={() => setTrail([...trail, { id: project.id, name: project.name }])}
            />
          ))}

          {lessons?.items.map((lesson) => (
            <LibraryItemCard
              key={lesson.id}
              name={lesson.name}
              imageUrl={lesson.imageUrl}
              subtitle={format(
                lesson.slideIds.length === 1 ? t["library.slideOne"] : t["library.slideOther"],
                { count: lesson.slideIds.length }
              )}
              onClick={() =>
                onOpenLesson({
                  id: lesson.id,
                  name: lesson.name,
                  slideCount: lesson.slideIds.length,
                  parentId: parent?.id ?? null,
                  path: [...trail.map((folder) => folder.name), lesson.name].join(" "),
                })
              }
            />
          ))}

          {projects?.items.length === 0 && lessons?.items.length === 0 && (
            <p className="body-sm text-app-muted col-span-full">{t["library.empty"]}</p>
          )}
        </div>
      )}

      {truncated && (
        <p className="caption text-app-muted">
          {format(t["library.truncated"], { count: PAGE_SIZE })}
        </p>
      )}
    </div>
  );
}
